import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Screen, Wordmark } from '../components/AppShell';
import {
  RegPlate, StatusText, StatusPill, Icon, Skeleton, EmptyState, Badge, SectionHeader,
} from '../components/ui';
import { api, type Dashboard as DashboardData, type DueItem, type Customer } from '../lib/api';
import { useLoad, useAction } from '../lib/hooks';
import { useAuth } from '../store/auth';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

export default function Dashboard() {
  const tenant = useAuth((s) => s.tenant);
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const { data, loading, error, reload } = useLoad<DashboardData>(() => api.get('/dashboard'));

  const today = useMemo(
    () => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }),
    [],
  );

  const firstRun = Boolean(data && data.counts.assets === 0);

  // "Next up" promotes one item; the sections below must not repeat it.
  const resume = data?.inProgress[0];
  const remainingInProgress = data?.inProgress.slice(1) ?? [];

  return (
    <Screen
      onRefresh={reload}
      // The workshop name is the identity of the screen; the greeting is the
      // passing detail, so it sits underneath rather than taking the title.
      title={tenant?.name ?? 'Today'}
      subtitle={`${greeting()}, ${user?.name?.split(' ')[0] ?? ''} · ${today}`}
      action={
        !firstRun ? (
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/fleet?start=1')}>
            <Icon name="plus" size={17} />
            Inspect
          </button>
        ) : undefined
      }
    >
      {loading && !data ? (
        <div className="stack-5">
          <Skeleton variant="hero" />
          <Skeleton rows={4} />
        </div>
      ) : null}

      {error ? (
        <div className="notice notice-danger" role="alert">
          {error}
        </div>
      ) : null}

      {data && firstRun ? <FirstRun tenantHasDetails={Boolean(tenant?.address)} /> : null}

      {data && !firstRun ? (
        <div className="stack-6">
          <Attention data={data} />
          <NextUp data={data} resume={resume} />

          {remainingInProgress.length > 0 && (
            <section className="stack">
              <SectionHeader
                title="Also in progress"
                count={remainingInProgress.length}
                action={<Link to="/inspections">All <Icon name="chevron" size={14} /></Link>}
              />
              <div className="panel divide">
                {remainingInProgress.map((insp) => (
                  <Link key={insp.id} to={`/inspections/${insp.id}/run`} className="listrow">
                    <div className="listrow-lead">
                      <RegPlate reg={insp.reg_no} fleetNo={insp.fleet_no} size="sm" />
                    </div>
                    <div className="grow">
                      <div className="listrow-title truncate">
                        {insp.signed_at ? 'Awaiting rectification' : 'Inspection in progress'}
                      </div>
                      <div className="listrow-sub">
                        <span className="mono">{insp.ref}</span>
                      </div>
                    </div>
                    {insp.status === 'defects' ? (
                      <StatusPill state="defects" size="sm">Defects</StatusPill>
                    ) : (
                      <StatusText state="draft">Draft</StatusText>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="stack">
            <SectionHeader
              title="Coming due"
              action={<Link to="/planner">Planner <Icon name="chevron" size={14} /></Link>}
            />

            {data.due.length === 0 ? (
              <EmptyState
                title="Nothing due in the next 60 days"
                body="Inspection, MOT and calibration dates are all clear."
              />
            ) : (
              <div className="panel divide">
                {data.due.slice(0, 8).map((item) => (
                  <Link key={`${item.assetId}-${item.key}`} to={`/fleet/${item.assetId}`} className="listrow">
                    <div className="listrow-lead">
                      <RegPlate reg={item.regNo} fleetNo={item.fleetNo} size="sm" />
                    </div>
                    <div className="grow">
                      <div className="listrow-title truncate">{item.title}</div>
                      <div className="listrow-sub">
                        <span className="truncate">{item.customerName ?? 'No customer'}</span>
                      </div>
                    </div>
                    {item.state === 'overdue' ? (
                      <StatusPill state="overdue" size="sm">{item.label}</StatusPill>
                    ) : (
                      <StatusText state={item.state}>{item.label}</StatusText>
                    )}
                  </Link>
                ))}
                {data.due.length > 8 && (
                  <Link to="/planner" className="listrow listrow-more">
                    {data.due.length - 8} more in the planner
                  </Link>
                )}
              </div>
            )}
          </section>

          {data.openDefects.length > 0 && (
            <section className="stack">
              <SectionHeader
                title="Open defects"
                count={data.openDefects.length}
                action={<Link to="/defects">All <Icon name="chevron" size={14} /></Link>}
              />
              <div className="panel divide">
                {data.openDefects.slice(0, 5).map((defect) => (
                  <Link
                    key={defect.id}
                    to={defect.inspection_id ? `/inspections/${defect.inspection_id}/run` : `/fleet/${defect.asset_id}`}
                    className="listrow"
                  >
                    <div className="listrow-lead">
                      <RegPlate reg={defect.reg_no} fleetNo={defect.fleet_no} size="sm" />
                    </div>
                    <div className="grow">
                      <div className="listrow-title truncate">{defect.description}</div>
                      <div className="listrow-sub">
                        {defect.im_no ? <Badge>IM {defect.im_no}</Badge> : null}
                        <Badge tone={defect.severity}>
                          {defect.severity === 'safety' ? 'Safety' : defect.severity === 'repair' ? 'Repair' : 'Monitor'}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <footer className="dash-foot">
            <Wordmark size="sm" />
            <p className="tiny dim">
              {data.counts.assets} on your fleet · {data.counts.vehicles} powered,{' '}
              {data.counts.trailers} trailer{data.counts.trailers === 1 ? '' : 's'}
            </p>
          </footer>
        </div>
      ) : null}
    </Screen>
  );
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/* ------------------------------------------------------------------ *
 * Attention band
 *
 * One statement, then the breakdown on a single scrolling line. It used to
 * wrap to two ragged rows and, with the gap beneath it, pushed the first
 * actionable row 38% of the way down the screen.
 * ------------------------------------------------------------------ */

function Attention({ data }: { data: DashboardData }) {
  const { overdue, dueSoon, safetyDefects, openDefects, inProgress } = data.counts;
  const attention = overdue + dueSoon + safetyDefects;

  let tone: 'ok' | 'warn' | 'danger' = 'ok';
  if (overdue > 0 || safetyDefects > 0) tone = 'danger';
  else if (dueSoon > 0) tone = 'warn';

  const parts: { label: string; to: string; state: string }[] = [];
  if (overdue) parts.push({ label: `${overdue} overdue`, to: '/planner', state: 'overdue' });
  if (dueSoon) parts.push({ label: `${dueSoon} due this week`, to: '/planner', state: 'due' });
  if (safetyDefects)
    parts.push({ label: `${safetyDefects} safety`, to: '/defects', state: 'overdue' });
  if (openDefects > safetyDefects)
    parts.push({
      label: `${openDefects - safetyDefects} repair${openDefects - safetyDefects === 1 ? '' : 's'}`,
      to: '/defects',
      state: 'due',
    });
  if (inProgress)
    parts.push({ label: `${inProgress} in progress`, to: '/inspections', state: 'unknown' });

  return (
    <div className={`attention attention-${tone}`}>
      <div className="attention-headline">
        {attention > 0 ? <span className="attention-number">{attention}</span> : null}
        <span className={attention > 0 ? 'title' : 'display'}>
          {attention === 0
            ? 'Everything in date'
            : attention === 1
              ? 'needs attention'
              : 'need attention'}
        </span>
      </div>

      {parts.length > 0 ? (
        <div className="attention-parts">
          {parts.map((p) => (
            <Link key={p.label} to={p.to} className="attention-chip">
              <StatusText state={p.state}>{p.label}</StatusText>
            </Link>
          ))}
        </div>
      ) : (
        <p className="small muted">
          No inspections, MOTs or calibrations due in the next 60 days, and no open defects.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Next up
 *
 * The single most urgent thing, with the action attached. A dashboard that
 * only reports numbers makes the user go and find the work; this hands it
 * to them.
 * ------------------------------------------------------------------ */

function NextUp({
  data,
  resume,
}: {
  data: DashboardData;
  /** Anything already on a bench outranks anything not yet started. */
  resume?: DashboardData['inProgress'][number];
}) {
  const navigate = useNavigate();
  const { busy, error, run } = useAction();

  const due: DueItem | undefined = data.due.find((d) => d.state === 'overdue' || d.state === 'due');

  if (!resume && !due) return null;

  const startInspection = (assetId: string) =>
    run(async () => {
      try {
        const r = await api.post<{ inspection: { id: string } }>('/inspections', { assetId });
        navigate(`/inspections/${r.inspection.id}/run`);
      } catch (err: any) {
        if (err?.status === 409 && err?.data?.existingId) {
          navigate(`/inspections/${err.data.existingId}/run`);
          return;
        }
        throw err;
      }
    });

  const regNo = resume ? resume.reg_no : due!.regNo;
  const fleetNo = resume ? resume.fleet_no : due!.fleetNo;
  const title = resume
    ? resume.signed_at
      ? 'Finish rectification'
      : 'Finish this inspection'
    : due!.title;
  const meta = resume ? resume.customer_name : due!.customerName;

  return (
    <section className="stack">
      <SectionHeader title="Next up" />
      <div className="nextup">
        <div className="nextup-head">
          <RegPlate reg={regNo} fleetNo={fleetNo} size="md" />
          {resume ? (
            <StatusPill state="defects" size="sm">In progress</StatusPill>
          ) : due!.state === 'overdue' ? (
            <StatusPill state="overdue" size="sm">{due!.label}</StatusPill>
          ) : (
            <StatusText state={due!.state}>{due!.label}</StatusText>
          )}
        </div>

        <div>
          <div className="nextup-title">{title}</div>
          <div className="nextup-meta">
            {meta ?? 'No customer'}
            {!resume && due!.due ? ` · ${formatDate(due!.due)}` : ''}
          </div>
        </div>

        {error ? <div className="notice notice-danger">{error}</div> : null}

        {resume ? (
          <Link to={`/inspections/${resume.id}/run`} className="btn btn-primary btn-block">
            <Icon name="clipboard" size={18} />
            Resume {resume.ref}
          </Link>
        ) : due!.key === 'next_inspection_date' ? (
          <button
            className="btn btn-primary btn-block"
            disabled={busy}
            onClick={() => startInspection(due!.assetId)}
          >
            <Icon name="clipboard" size={18} />
            {busy ? 'Opening…' : 'Start inspection'}
          </button>
        ) : (
          <Link to={`/fleet/${due!.assetId}`} className="btn btn-primary btn-block">
            View vehicle
          </Link>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * First run
 * ------------------------------------------------------------------ */

function FirstRun({ tenantHasDetails }: { tenantHasDetails: boolean }) {
  const { data: customers } = useLoad<{ customers: Customer[] }>(() => api.get('/customers'));
  const hasCustomer = (customers?.customers.length ?? 0) > 0;

  const steps = [
    {
      title: 'Workshop details',
      sub: 'Address and phone, printed on every record',
      to: '/settings',
      done: tenantHasDetails,
    },
    {
      title: 'Add a customer',
      sub: 'The operator whose vehicles you inspect',
      to: '/customers/new',
      done: hasCustomer,
    },
    {
      title: 'Add a vehicle or trailer',
      sub: 'Registration, interval and renewal dates',
      to: '/fleet/new',
      done: false,
    },
    {
      title: 'Run the first inspection',
      sub: 'Full RHA sheet, signed and locked',
      to: '/fleet?start=1',
      done: false,
    },
  ];
  const nextIndex = steps.findIndex((s) => !s.done);

  return (
    <div className="setup">
      <div>
        <h2 className="display">Set up your fleet</h2>
        <p className="muted" style={{ marginTop: 'var(--s2)' }}>
          Four steps and you're inspecting. Takes about five minutes.
        </p>
      </div>

      <div className="setup-steps">
        {steps.map((step, i) => (
          <Link
            key={step.title}
            to={step.to}
            className={`setup-step${step.done ? ' is-done' : ''}${i === nextIndex ? ' is-next' : ''}`}
          >
            <span className="setup-num">{step.done ? <Icon name="check" size={16} /> : i + 1}</span>
            <span className="grow">
              <span className="setup-step-title">{step.title}</span>
              <span className="setup-step-sub" style={{ display: 'block' }}>
                {step.sub}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
